import { ErrorBoundary } from '@red-digital/bricks';
import { useEffect } from 'react';
import { getAnonymousID } from '~/helper/parseURLParams';
import { CohesionProvider } from '@redventures/cohesion-utils-react';
import { ExperienceDataProvider } from '~/cohesion/Monarch/ExperienceDataProvider';
import type { AppProps } from 'next/app';
import type { NextWebVitalsMetric } from 'next/app';
import Head from 'next/head';
import { recursivelyUpdateLinks } from '~/helper/trackURLParams';
import { getGCLID } from '~/lib/segment/gclid';
import useParamsTracking from '~/lib/hooks/useParamsTracking';
import ReducedMotionContext from '~/lib/ReducedMotionProvider';
import useReduceMotion from '~/helper/reduceMotion';
import '~/styles/globals.css'; // imports tailwind
import '~/styles/globals.scss';
import '~/styles/bricks/bricks.scss';
import { componentLookup } from '~/helper/componentLookup';
import NewRelic from '~/lib/newrelic/NewRelic';

if (typeof window !== 'undefined') {
  const gclid = getGCLID();

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof window === 'undefined') return;

    const body = document.querySelector<HTMLBodyElement>('body');

    if (!body) return;

    recursivelyUpdateLinks(body.childNodes, gclid);

    // MutationObserver calls a given callback function whenever
    // a desired DOM Node's children are updated. In this case,
    // we watch the `body` element, and run recursivelyUpdateLinks
    // when that happens. Hover over recursivelyUpdateLinks for more details
    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          recursivelyUpdateLinks(mutation.addedNodes, gclid);
        }
      }
    });
    observer.observe(body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  });
}

const pages = {
  '/': { page: '/' },
  '/health': { page: '/health' },
  '/assist': { page: '/assist' },
  '/student': { page: '/student' },
};
export async function getStaticPaths() {
  const paths = Object.keys(pages).map((page) => ({
    params: { page },
  }));

  return { paths, fallback: false };
}

function App({ Component, pageProps }: AppProps) {
  useParamsTracking();

  const isReducedMotion = useReduceMotion();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Beacon pageview event for W+ tracking
    if (typeof window._bcq !== 'undefined') {
      const data = {
        nm: {
          nm: 'rvLandingPage',
        },
        co: {
          ty: 'RV MLP',
          mx: getAnonymousID(),
        },
        mb: {
          st: '',
          ms: '',
        },
      };
      window._bcq.push([
        '_tagAction',
        'wplus',
        'pageView',
        'rvLandingPage',
        data,
      ]);
    }
  }, []);

  return (
    <ErrorBoundary
      production={process.env.environment === 'production'}
      title='Client Error'
      message='An unexpected error occurred that prevented this page from loading.'
    >
      <ReducedMotionContext.Provider value={{ isReducedMotion }}>
        <Head>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1, viewport-fit=cover'
          />
        </Head>

        {/* New relic must be rendered in _app.tsx outside of the <Head> */}
        {/* If placed in _document.tsx (head or body) the script will fail to inject to the DOM */}
        <NewRelic />

        <CohesionProvider components={componentLookup}>
          <ExperienceDataProvider>
            <Component {...pageProps} />
          </ExperienceDataProvider>
        </CohesionProvider>
      </ReducedMotionContext.Provider>
    </ErrorBoundary>
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (process.env.NEXT_PUBLIC_WALMART_NODE_ENV !== 'production') {
    return;
  }

  if (metric.label === 'web-vital') {
    // Import and use cwv functions
    import('~/lib/helper/cwv.js').then(({ default: sendToAnalytics }) => {
      sendToAnalytics(metric);
    });
  }
}

export default App;
